// src/components/ProtectedRoute.jsx
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Auth } from "aws-amplify";
import { Spinner } from "react-bootstrap";

// Same logic you posted (Admin OR Support)
function isSupportUser(claims) {
  const grp =
    claims?.["cognito:groups"] ||
    claims?.["groups"] ||
    claims?.["custom:groups"] ||
    null;

  if (!grp) return false;
  let groups = Array.isArray(grp) ? grp : String(grp).split(",");
  groups = groups.map((g) => g.replace(/[\[\]]/g, "").trim());

  return groups.includes("Admin") || groups.includes("Support");
}

export default function ProtectedRoute({
  children,
  requireSub = false,
  requireSupport = false, // 🆕 Admin/Support gate
  hasActiveSub,
  subChecked,
}) {
  const [checking, setChecking] = useState(true);
  const [isAuthed, setIsAuthed] = useState(false);
  const [isAllowed, setIsAllowed] = useState(true); // for requireSupport
  const location = useLocation();

  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        if (!isMounted) return;

        setIsAuthed(true);

        // 🆕 If route requires Admin/Support, evaluate claims
        if (requireSupport) {
          const claims = user?.signInUserSession?.idToken?.payload || {};
          setIsAllowed(isSupportUser(claims));
        } else {
          setIsAllowed(true);
        }
      } catch {
        if (!isMounted) return;
        setIsAuthed(false);
        setIsAllowed(false);
      } finally {
        if (isMounted) setChecking(false);
      }
    };

    checkAuth();
    return () => {
      isMounted = false;
    };
  }, [requireSupport]);

  // ⏳ While we check Amplify *or* haven't checked subscription yet
  if (checking || (requireSub && !subChecked)) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" />
        <span className="ms-2">Checking your access…</span>
      </div>
    );
  }

  // ❌ Not logged in → go to login
  if (!isAuthed) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  // ❌ Logged in but not Admin/Support
  if (requireSupport && !isAllowed) {
    return <Navigate to="/" replace />;
  }

  // 🔐 Only after subChecked is true do we enforce requireSub
if (requireSub && !hasActiveSub) {
  return (
    <Navigate
      to="/plans"
      replace
      state={{ from: location.pathname + location.search }}
    />
  );
}


  // ✅ Logged in (and subscribed if required, and support/admin if required)
  return children;
}
